// Campana de notificaciones in-app del módulo Mantenimiento.
//
//   - Polling 30s (useNotificacionesMtto) sobre GET /notificaciones.
//   - Badge con el conteo de no leídas.
//   - Panel click-based (no hover: en tablet de planta el hover no existe).
//   - Toast al detectar ids NUEVOS entre polls (skip del primer load para no
//     bombardear al entrar) con CTA a /mantenimiento/lineas.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { NotificacionMtto } from 'schemas';
import { Icon } from '../../components/ui/icon';
import { Spinner } from '../../components/ui/spinner';
import { cn } from '../../lib/utils';
import { useMarcarNotificacionesLeidas, useNotificacionesMtto } from './queries';

function tiempoRelativo(iso: string): string {
  const min = Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 60_000));
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.floor(h / 24)} d`;
}

export function NotificacionesCampana() {
  const navigate = useNavigate();
  const notificaciones = useNotificacionesMtto();
  const marcarLeidas = useMarcarNotificacionesLeidas();
  const [abierto, setAbierto] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const idsPreviosRef = useRef<Set<string> | null>(null);

  const items: NotificacionMtto[] = notificaciones.data?.items ?? [];
  const noLeidas = notificaciones.data?.no_leidas ?? 0;

  // Toast SOLO para ids nuevos entre polls (el primer load no notifica).
  useEffect(() => {
    if (!notificaciones.data) return;
    const idsActuales = new Set(notificaciones.data.items.map((n) => n.id));
    if (idsPreviosRef.current !== null) {
      for (const n of notificaciones.data.items) {
        if (!idsPreviosRef.current.has(n.id) && n.read_at === null) {
          toast(n.titulo, {
            description: n.cuerpo ?? undefined,
            action:
              n.tipo === 'paro_registrado'
                ? { label: 'Ver líneas', onClick: () => void navigate('/mantenimiento/lineas') }
                : undefined,
          });
        }
      }
    }
    idsPreviosRef.current = idsActuales;
  }, [notificaciones.data, navigate]);

  // Cerrar al click afuera.
  useEffect(() => {
    if (!abierto) return;
    function onDown(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setAbierto(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [abierto]);

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        aria-label={noLeidas > 0 ? `Notificaciones: ${noLeidas} sin leer` : 'Notificaciones'}
        onClick={() => setAbierto((v) => !v)}
        className={cn(
          'relative flex size-9 items-center justify-center rounded border border-border text-fg-secondary transition-colors hover:text-fg',
          abierto && 'bg-surface-elevated text-fg',
        )}
      >
        <Icon name="bell" className="size-4" />
        {noLeidas > 0 ? (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-crit px-1 text-[10px] font-medium text-white">
            {noLeidas > 99 ? '99+' : noLeidas}
          </span>
        ) : null}
      </button>

      {abierto ? (
        <>
          {/* Backdrop en móvil: tap fuera cierra y enfoca el panel. */}
          <div
            className="fixed inset-0 z-40 bg-black/50 sm:hidden"
            aria-hidden
            onClick={() => setAbierto(false)}
          />
          {/* Panel: hoja ancha en móvil (fácil de leer y tocar), popover en desktop. */}
          <div className="fixed inset-x-3 top-16 z-50 flex max-h-[75vh] flex-col overflow-hidden rounded-lg border border-border bg-surface-elevated shadow-xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-11 sm:max-h-96 sm:w-80 sm:rounded">
            <div className="flex items-center justify-between border-b border-border px-4 py-3 sm:px-3 sm:py-2">
              <p className="text-sm font-medium text-fg sm:text-xs">Notificaciones</p>
              {noLeidas > 0 ? (
                <button
                  type="button"
                  className="text-xs text-accent hover:underline sm:text-[11px]"
                  onClick={() => marcarLeidas.mutate(undefined)}
                  disabled={marcarLeidas.isPending}
                >
                  Marcar todas como leídas
                </button>
              ) : null}
            </div>
            <div className="flex-1 overflow-y-auto">
              {notificaciones.isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner className="size-5" />
                </div>
              ) : items.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-fg-tertiary sm:py-8 sm:text-xs">
                  Sin notificaciones por ahora.
                </p>
              ) : (
                <ul>
                  {items.map((n) => (
                    <li key={n.id}>
                      <button
                        type="button"
                        className={cn(
                          'flex w-full flex-col gap-1 border-b border-border/60 px-4 py-3 text-left transition-colors hover:bg-surface-base sm:gap-0.5 sm:px-3 sm:py-2',
                          n.read_at === null && 'bg-accent-soft/40',
                        )}
                        onClick={() => {
                          if (n.read_at === null) marcarLeidas.mutate([n.id]);
                          if (n.tipo === 'paro_registrado') {
                            setAbierto(false);
                            void navigate('/mantenimiento/lineas');
                          }
                        }}
                      >
                        <span className="flex items-center gap-2 sm:gap-1.5">
                          {n.read_at === null ? (
                            <span className="size-2 shrink-0 rounded-full bg-accent sm:size-1.5" />
                          ) : null}
                          <span className="text-sm font-medium text-fg sm:text-xs">{n.titulo}</span>
                        </span>
                        {n.cuerpo ? (
                          <span className="text-xs text-fg-secondary sm:line-clamp-2 sm:text-[11px]">
                            {n.cuerpo}
                          </span>
                        ) : null}
                        <span className="text-[11px] text-fg-tertiary sm:text-[10px]">
                          {tiempoRelativo(n.created_at)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
